import { ReactNode } from "react";

import Button, { ButtonVariant } from "@/components/ui/button";
import cn from "@/utils/core/cn";

export type GroupButton<T> = {
  value: T;
  label: string | ReactNode;
  href?: string;
  className?: string;
};

type Props<T> = {
  value: T;
  buttons: GroupButton<T>[];
  onChange: (value: T) => void;
  onClick?: (value: string | ReactNode) => void;
  variant?: ButtonVariant;
  activeVariant?: ButtonVariant;
  className?: string;
  activeClassName?: string;
  containerClassName?: string;
  disabled?: boolean;
};

const ButtonGroup = <T extends string>({
  value,
  buttons,
  onChange,
  onClick,
  variant,
  activeVariant = "primary",
  className,
  containerClassName,
  activeClassName,
  disabled,
}: Props<T>) => {
  return (
    <div className={cn("flex", containerClassName)}>
      {buttons.map((button, index) => (
        <Button
          key={button.value}
          variant={button.value === value ? activeVariant : variant}
          disabled={disabled}
          onClick={() => {
            onChange(button.value);
            onClick?.(button.label);
          }}
          href={button.href}
          className={cn(
            "relative hover:z-10 focus:z-20",
            buttons.length > 1 &&
              (index === 0
                ? "rounded-r-none"
                : index !== buttons.length - 1
                  ? "ml-[-1px] rounded-none"
                  : "ml-[-1px] rounded-l-none"),
            value === button.value ? activeClassName : className,
            button.className
          )}
        >
          {button.label}
        </Button>
      ))}
    </div>
  );
};

export default ButtonGroup;
